import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ClipboardEvent, DragEvent, ReactNode, RefObject } from 'react'
import { FileText, Image as ImageIcon, Plus, Trash2, UploadCloud } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import type { AttachmentAdapter, AttachmentItem } from '@/services/attachments/attachmentTypes'
import { removeAfterConfirmation } from './attachmentActions'
import { handleClipboardImages } from './clipboardImages'
import { ImageLightbox } from './ImageLightbox'

export type PendingUpload = {
  key: string
  file: File
  previewUrl: string
  status: 'uploading' | 'error'
  errorMessage?: string
}

type AttachmentGalleryProps = {
  entityType: string
  entityId: string
  adapter: AttachmentAdapter
  readOnly?: boolean
  onFilesChanged?: () => void
}

type ImageViewerItem = {
  name: string
  url: string
}

export function AttachmentGallery({
  entityType,
  entityId,
  adapter,
  readOnly = false,
  onFilesChanged,
}: AttachmentGalleryProps) {
  const [attachments, setAttachments] = useState<AttachmentItem[]>([])
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [clipboardNotice, setClipboardNotice] = useState('')
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewUrlsRef = useRef(new Set<string>())

  const imageAttachments = useMemo(
    () => attachments.filter((attachment) => attachment.mimeType.startsWith('image/')),
    [attachments],
  )
  const imageViewerItems = useMemo<ImageViewerItem[]>(
    () => imageAttachments.map((attachment) => ({
      name: attachment.fileName,
      url: adapter.contentUrl(attachment),
    })),
    [adapter, imageAttachments],
  )

  const loadAttachments = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      setAttachments(await adapter.list(entityType, entityId))
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Không thể tải danh sách tệp.')
    } finally {
      setIsLoading(false)
    }
  }, [adapter, entityId, entityType])

  useEffect(() => {
    void loadAttachments()
  }, [loadAttachments])

  useEffect(() => () => {
    for (const previewUrl of previewUrlsRef.current) {
      URL.revokeObjectURL(previewUrl)
    }
  }, [])

  const addFiles = useCallback(async (files: File[]) => {
    if (files.length === 0 || readOnly) return

    const pendingBatch = files.map((file, index) => ({
      key: `${Date.now()}-${index}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading' as const,
    }))
    for (const pendingUpload of pendingBatch) {
      previewUrlsRef.current.add(pendingUpload.previewUrl)
    }

    setPendingUploads((current) => [...current, ...pendingBatch])
    setLoadError(null)

    try {
      const results = await adapter.upload(entityType, entityId, files)
      const uploadedItems = results.flatMap((result) => result.item ? [result.item] : [])
      const resultsByFile = new Map(results.map((result) => [result.file, result]))
      setAttachments((current) => [...current, ...uploadedItems])
      setPendingUploads((current) => current.flatMap((pendingUpload) => {
        if (!pendingBatch.some((candidate) => candidate.key === pendingUpload.key)) {
          return [pendingUpload]
        }

        const result = resultsByFile.get(pendingUpload.file)
        if (result?.item) {
          URL.revokeObjectURL(pendingUpload.previewUrl)
          previewUrlsRef.current.delete(pendingUpload.previewUrl)
          return []
        }

        return [{
          ...pendingUpload,
          status: 'error' as const,
          errorMessage: result?.error ?? 'Không thể tải tệp lên.',
        }]
      }))

      if (uploadedItems.length > 0) onFilesChanged?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Không thể tải tệp lên.'
      setPendingUploads((current) => current.map((pendingUpload) => (
        pendingBatch.some((candidate) => candidate.key === pendingUpload.key)
          ? { ...pendingUpload, status: 'error', errorMessage: message }
          : pendingUpload
      )))
    }
  }, [adapter, entityId, entityType, onFilesChanged, readOnly])

  const handlePaste = useCallback((event: ClipboardEvent<HTMLElement>) => {
    if (readOnly) return
    handleClipboardImages(
      event.clipboardData,
      () => event.preventDefault(),
      (files) => void addFiles(files),
      () => {
        setClipboardNotice('Clipboard không chứa ảnh')
        window.setTimeout(() => setClipboardNotice(''), 2500)
      },
    )
  }, [addFiles, readOnly])

  const handleDrop = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    void addFiles(Array.from(event.dataTransfer.files))
  }, [addFiles])

  const removeAttachment = useCallback(async (attachment: AttachmentItem) => {
    try {
      const removed = await removeAfterConfirmation(
        attachment.fileName,
        (message) => window.confirm(message),
        () => adapter.remove(attachment.id),
      )
      if (!removed) return
      setAttachments((current) => current.filter((item) => item.id !== attachment.id))
      onFilesChanged?.()
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Không thể xóa tệp.')
    }
  }, [adapter, onFilesChanged])

  return (
    <FileDropPasteZone onPaste={handlePaste} onDrop={handleDrop}>
      <AttachmentToolbar
        readOnly={readOnly}
        fileInputRef={fileInputRef}
        onFilesSelected={(files) => void addFiles(files)}
      />

      {clipboardNotice ? <p role="status" className="mb-2 text-xs text-amber-700">{clipboardNotice}</p> : null}
      <AttachmentGalleryStatus
        isLoading={isLoading}
        error={loadError}
        isEmpty={attachments.length === 0 && pendingUploads.length === 0}
        onRetry={() => void loadAttachments()}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        {attachments.map((attachment) => {
          const imageIndex = imageAttachments.findIndex((image) => image.id === attachment.id)
          return (
            <AttachmentThumbnail
              key={attachment.id}
              attachment={attachment}
              imageIndex={imageIndex}
              readOnly={readOnly}
              contentUrl={adapter.contentUrl(attachment)}
              onOpenImage={setActiveImageIndex}
              onRemove={() => void removeAttachment(attachment)}
            />
          )
        })}
        {pendingUploads.map((pendingUpload) => (
          <PendingUploadThumbnail key={pendingUpload.key} pendingUpload={pendingUpload} />
        ))}
      </div>

      {activeImageIndex !== null ? (
        <ImageLightbox
          images={imageViewerItems}
          index={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
          onChange={setActiveImageIndex}
        />
      ) : null}
    </FileDropPasteZone>
  )
}

function FileDropPasteZone({
  children,
  onPaste,
  onDrop,
}: {
  children: ReactNode
  onPaste: (event: ClipboardEvent<HTMLElement>) => void
  onDrop: (event: DragEvent<HTMLElement>) => void
}) {
  return (
    <section
      tabIndex={0}
      aria-label="Ảnh và tài liệu"
      onPaste={onPaste}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
      className="rounded-lg border border-slate-200 bg-white p-4 outline-none focus:ring-2 focus:ring-blue-200"
    >
      {children}
    </section>
  )
}

export function AttachmentToolbar({
  readOnly,
  fileInputRef,
  onFilesSelected,
}: {
  readOnly: boolean
  fileInputRef: RefObject<HTMLInputElement | null>
  onFilesSelected: (files: File[]) => void
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <h2 className="text-base font-semibold text-slate-900">ẢNH &amp; TÀI LIỆU</h2>
        <p className="text-xs text-slate-500">Có thể Ctrl+V để dán ảnh trực tiếp</p>
      </div>
      {!readOnly ? (
        <>
          <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()}>
            <Plus className="mr-1 h-4 w-4" />
            Thêm ảnh / tài liệu
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            className="hidden"
            onChange={(event) => {
              onFilesSelected(Array.from(event.target.files ?? []))
              event.target.value = ''
            }}
          />
        </>
      ) : null}
    </div>
  )
}

export function AttachmentThumbnail({
  attachment,
  imageIndex,
  readOnly,
  contentUrl,
  onOpenImage,
  onRemove,
}: {
  attachment: AttachmentItem
  imageIndex: number
  readOnly: boolean
  contentUrl: string
  onOpenImage: (index: number) => void
  onRemove: () => void
}) {
  const isImage = imageIndex >= 0

  return (
    <article className="group relative overflow-hidden rounded-md border border-slate-200 bg-slate-50">
      {isImage ? (
        <button
          type="button"
          className="block w-full"
          aria-label={`Xem ảnh ${attachment.fileName}`}
          onClick={() => onOpenImage(imageIndex)}
        >
          <img src={contentUrl} loading="lazy" alt={attachment.fileName} className="h-28 w-full object-cover" />
        </button>
      ) : (
        <a
          href={attachment.mimeType === 'application/pdf' ? contentUrl : undefined}
          target="_blank"
          rel="noreferrer"
          className="flex h-28 flex-col items-center justify-center gap-2 p-2 text-center text-xs text-slate-700"
        >
          <FileText className="h-7 w-7 text-slate-500" />
          {attachment.fileName}
        </a>
      )}
      <p className="truncate px-2 py-1 text-xs" title={attachment.fileName}>{attachment.fileName}</p>
      {!readOnly ? (
        <button
          type="button"
          aria-label={`Xóa ${attachment.fileName}`}
          onClick={onRemove}
          className="absolute right-1 top-1 rounded bg-white/90 p-1 text-slate-600 hover:text-rose-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ) : null}
    </article>
  )
}

export function PendingUploadThumbnail({ pendingUpload }: { pendingUpload: PendingUpload }) {
  const isImage = pendingUpload.file.type.startsWith('image/')

  return (
    <article className="overflow-hidden rounded-md border border-slate-200 bg-slate-50">
      {isImage ? (
        <img src={pendingUpload.previewUrl} alt={pendingUpload.file.name} className="h-28 w-full object-cover" />
      ) : (
        <div className="flex h-28 items-center justify-center">
          <ImageIcon className="h-7 w-7 text-slate-500" />
        </div>
      )}
      <p className="truncate p-2 text-xs">
        {pendingUpload.status === 'uploading' ? 'Đang tải lên…' : pendingUpload.errorMessage}
      </p>
    </article>
  )
}

export function AttachmentGalleryStatus({
  isLoading,
  error,
  isEmpty,
  onRetry,
}: {
  isLoading: boolean
  error: string | null
  isEmpty: boolean
  onRetry: () => void
}) {
  if (error) return <PageError message={error} onRetry={onRetry} />
  if (isLoading) return <PageLoading label="Đang tải danh sách tệp..." />
  if (!isEmpty) return null

  return (
    <div className="flex min-h-24 flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 text-sm text-slate-500">
      <UploadCloud className="h-5 w-5" />
      Chưa có ảnh hoặc tài liệu
    </div>
  )
}
